import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { BlogPost } from '../../lib/blog';

interface BlogCardProps {
  post: BlogPost;
}

export const BlogCard: React.FC<BlogCardProps> = ({ post }) => {
  // Root-relative and resolved at render time, so the href is present in the
  // pre-rendered HTML for crawlers. The blog subdomain no longer serves
  // articles (it 301s to /blog/<slug>), so there is no host to branch on.
  const postUrl = `/blog/${post.slug}`;
  const postImage = post.image || "/og-image.png";

  return (
    <article className="bg-[#192038] rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:-translate-y-1">
      <Link href={postUrl} className="block">
        <div className="relative h-48 w-full">
          <Image
            src={postImage}
            alt={post.title}
            fill
            className="object-cover"
            sizes="(max-width: 1050px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        </div>
        
        <div className="p-6">
          <div className="flex flex-wrap gap-2 mb-3">
            {post.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2 py-1 bg-[#9945FF] text-white text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
          
          <h2 className="text-xl font-bold text-white mb-3 line-clamp-2 hover:text-[#9945FF] transition-colors">
            {post.title}
          </h2>
          
          <p className="text-gray-300 text-sm mb-4 line-clamp-3">
            {post.description}
          </p>
          
          <div className="flex items-center justify-between text-sm text-gray-400">
            <div className="flex items-center space-x-4">
              <span>{post.author}</span>
              <span>•</span>
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </div>
            <span>{post.readingTime}</span>
          </div>
        </div>
      </Link>
    </article>
  );
};

